const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/soh/eCheckin/sections/components/KontaktPersonBlock.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../../translations';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { FieldPathModifier } from 'App/components/eCheckin/types';
import { createFieldPath, fieldValueIsNot } from 'App/components/eCheckin/utils';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../../common/translations';
import {
    AddressBlock,
    AddressFieldsConfig,
    defaultAddressFieldsConfig,
} from 'App/components/eCheckin/components/fields/AddressBlock';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { BeziehungField } from 'App/components/eCheckin/components/fields/metakat/BeziehungField';

type Props = {
    basePath: string;
    fieldPathModifier?: FieldPathModifier; // can be used in the current structure to indicate second (or third) contact person
    hidden?: boolean;
    // when set to not required, this will make the Beziehung field mandatory and other fields are shown.
    // When set to not required the field will get a "Keine Angabe" value and other fields are not shown until a value is specified.
    required?: boolean;
};

export const KontaktPersonBlock = forwardRef((props: Props, ref: any) => {
    const { basePath, fieldPathModifier } = props;

    const PATH_BEZIEHUNG = createFieldPath(basePath, 'beziehung', fieldPathModifier);
    const PATH_PRIORITY = createFieldPath(basePath, 'priority', fieldPathModifier);
    const PATH_FIRST_NAME = createFieldPath(basePath, 'firstname', fieldPathModifier);
    const PATH_LAST_NAME = createFieldPath(basePath, 'lastname', fieldPathModifier);
    const PATH_COUNTRY = createFieldPath(basePath, 'country', fieldPathModifier);
    const PATH_TEL = createFieldPath(basePath, 'tel', fieldPathModifier);

    const hidden = props.hidden ?? false;
    const required = props.required ?? false;

    const kontaktPersonBlockRef = useCIS4m(ref);

    const formData = useCIS4mData();

    const hideFields = !fieldValueIsNot(formData, PATH_BEZIEHUNG, ['', undefined]);

    const addressFieldsConfig: AddressFieldsConfig = {
        streetAndNumber: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.streetAndNumber.path) : undefined,
            required: false,
        },
        additionalInfo: {
            included: false,
        },
        postalCode: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.postalCode.path) : undefined,
            required: false,
        },
        location: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.location.path) : undefined,
            maxLength: 30,
            required: false,
        },
        country: {
            path: fieldPathModifier ? fieldPathModifier(defaultAddressFieldsConfig.country.path) : undefined,
            required: true,
        },
    };

    return (
        <CIS4mBlock ref={kontaktPersonBlockRef} hidden={hidden}>
            <BeziehungField label={textMap.beziehung} path={PATH_BEZIEHUNG} required={required} />
            <CIS4mBlock hidden={hideFields}>
                <CIS4mSingleCheckbox label={textMap.priorityContact} path={PATH_PRIORITY} />
                <CIS4mBlock>
                    <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required={true} />
                    <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required={true} />
                    <AddressBlock basePath={basePath} required={true} fieldsConfig={addressFieldsConfig} />
                    <PhoneNumberField
                        label={textMap.tel}
                        path={PATH_TEL}
                        countryCodePath={PATH_COUNTRY}
                        required={true}
                    />
                </CIS4mBlock>
            </CIS4mBlock>
        </CIS4mBlock>
    );
});

KontaktPersonBlock.displayName = 'KontaktPersonBlock';

export default KontaktPersonBlock;
